import { useCallback, useState } from 'react'

import { AttachmentUploadType } from '@tribeplatform/gql-client/types'

import { FileSizeLimitExceededError } from '../../types/file.size.error.js'
import { readFiles, useCreateFiles, useNetwork } from '../index.js'

const DEFAULT_FILE_SIZE_LIMIT_MB = 50 * 1024 * 1024 // 100MB

export const useFileUploader = () => {
  const { mutateAsync: createFiles, isLoading } = useCreateFiles()
  const [loadingFiles, setLoadingFiles] = useState<File[]>([])
  const { data: network } = useNetwork()
  const fileSizeLimit =
    network?.settings?.fileSizeLimit ?? DEFAULT_FILE_SIZE_LIMIT_MB

  const getLimitExceedingFiles = useCallback(
    (files: File[]) => {
      const sizeLimitExceededFiles = files.filter(
        file => file.size > fileSizeLimit,
      )

      return {
        sizeLimitExceededFiles,
        limit: fileSizeLimit,
      }
    },
    [fileSizeLimit],
  )

  const uploadFiles = useCallback(
    async (files: File[]) => {
      const { sizeLimitExceededFiles, limit } = getLimitExceedingFiles(files)

      if (sizeLimitExceededFiles.length > 0) {
        throw new FileSizeLimitExceededError('File size limit exceeded', limit)
      }

      const result = await readFiles<File>(files)
      setLoadingFiles(result)

      try {
        const uploadArgs: AttachmentUploadType = files.map(file => {
          const extension = file.name.split('.').pop()?.toLowerCase()
          return {
            file,
            size: file.size,
            contentType: file.type,
            name: file.name,
            extension,
          }
        })

        const uploadedFiles = await createFiles(uploadArgs)
        return uploadedFiles
      } catch (error) {
        setLoadingFiles([])
        throw error
      } finally {
        setLoadingFiles([])
      }
    },
    [createFiles, getLimitExceedingFiles],
  )

  const uploadFile = useCallback(
    async (file: File) => {
      const [uploadedFile] = await uploadFiles([file])
      return uploadedFile
    },
    [uploadFiles],
  )

  return {
    uploadFile,
    uploadFiles,
    loadingFiles,
    getLimitExceedingFiles,
    isLoading,
  }
}
